<template>
<div class="chapter">
<div class="inner_page">
<h1 class="chapter-number"><a id="page-31"></a><span href="#/toc#toc-chapter004" title="Link target outside of Sample">4</span></h1>
<p class="noindent"><span class="dropcap"><em>E</em></span>ventually, having watched the sister stamp angrily down to the first corner and disappear, the policeman got up wearily from
         his chair and wandered in. He ignored Petrovitch at first, and walked around, touching the furnishings, playing with the window
         controls, pouring himself a glass of water from the jug on the bedside table.
      </p>
<p>Petrovitch looked over the top of his glasses at the man as he drank, one gulp, two gulps, three.</p>
<p>“Do you mind if I sit down?” the man asked, wiping his mouth on his jacket sleeve, then sat down anyway without waiting for
         an answer. “There’s always too much standing up in this job.”
      </p>
<p>He patted his pockets for his warrant card, and passed it over to Petrovitch with an air of distraction: he was already looking
         for something else in a different place.
      </p>
<p>Petrovitch inspected the card: Chain, Henry—Detective Inspector, Metropolitan Police. The hologram looked twenty years out
         of date, because the Chain in front of him had far more wrinkles and much less hair. His head was flaring under the lights,
         the thin strands dotted <a id="page-32"></a>haphazardly over his scalp illuminated from below as well as above.
      </p>
<p>Petrovitch passed the card back, and Chain opened the cover of his police handheld. The detective chewed the stylus for a
         moment, then pecked at an icon.
      </p>
<p>“Right then,” said Chain, and interrupted himself with a volley of wet coughing. “Sorry. It’s the air. I’ll start again: Petrovitch,
         Samuil. Twenty-two, citizen of the Russian Federation, here on a university scholarship. Address, three-four-one-five, Clapham
         Transit A. You will stop me if I mess up here? I know these things are supposed to be accurate, but you know what it’s like.”
         He paused. “You do know what it’s like, don’t you?”
      </p>
<p>Petrovitch cleared his throat. “I know.”</p>
<p>“Your English good? Don’t need a translator or a dictionary?”</p>
<p>“I’m fluent.”</p>
<p>“This is just an interview, you know. You haven’t done anything wrong. I’m just asking a few questions. If you think you might
         need a lawyer, do say.” Chain coughed again, an episode that left him breathless. He twisted round in his chair and poured
         himself some more water. “Nice room.”
      </p>
<p>Petrovitch nodded slowly. Either the man was brilliant or a buffoon. Only time would tell which.</p>
<p>“You are Okay to answer a few questions, aren’t you? Doctors told me you’d died several times on the way here. I can come
         back later.” Chain touched the video icon on his handheld and hunted for the right clip.
      </p>
<p>“<em>Yobany stos!</em> Get on with it.”
      </p>
<p>Chain glanced up. “I know that one. Just so you know, <em>yeban’ko maloletnee</em>.”
      </p>
<p><a id="page-33"></a>Petrovitch chuckled, then grimaced at the discomfort. “Ask your questions, Detective.”
      </p>
<p>“This,” said Chain, “this is you, early this morning.” He passed Petrovitch the handheld.</p>
<p>Petrovitch watched himself, identified with a floating yellow tag, crawl along the pavement at Green Park. A red tag moved
         into view, and the two crossed briefly. The screen went blank.
      </p>
<p>“Where’s the rest?” he asked.</p>
<p>“The cameras over the whole block went down.” Chain took the handheld back. “Very professional. But we know what happened.
         We know where you went, and we know how it ended.”
      </p>
<p>He opened up another file, and showed Petrovitch a picture of two bullet-ridden gangsters lying in a mutual pool of thick
         red blood.
      </p>
<p>Petrovitch looked, then looked away. “If you know what happened, why do you need me?”</p>
<p>“We—I—was hoping you could tell me why. Why would Samuil Petrovitch risk his scrawny neck intervening in a kidnapping that
         has nothing to do with him? Or at least, seems to have nothing to do with him. You weren’t some sort of Plan B, were you?”
      </p>
<p>“Why don’t you ask them?” Petrovitch nodded at the screen. “They look like the sort of guys who could come up with a really
         good Plan B.”
      </p>
<p>“Point taken.” Chain reamed an eye with his finger until it squelched. “Do you know who it was you saved?”</p>
<p>“No. Never seen her before in my life.”</p>
<p>Chain pressed his lips together and ruminated. “If I had a euro for every time someone said that to me. “Oh, Detective, I
         have no idea whose body this is in the boot of <a id="page-34"></a>my car. Never seen her before in my life.” You genuinely don’t know?”
      </p>
<p>“No.”</p>
<p>“Don’t keep up with the celebrity news?”</p>
<p>“Do I look like someone who uses celeb porn?” Petrovitch grunted. “I study high-energy physics.”</p>
<p>The detective sighed. “She’s Sonja Oshicora. Ring any bells now?”</p>
<p>“No.”</p>
<p>“Oshicora Corporation?”</p>
<p>“No.”</p>
<p>“You heard what happened to Japan, right? The whole falling-into-the-sea thing?”</p>
<p>“I heard. It wasn’t my fault, though.”</p>
<p>“Very droll, Petrovitch. So, let’s just recap.” He dropped the handheld in his lap and held out his sausage-like fingers.
         “One, you were minding your own business, proceeding in a westerly direction on Green Park. Two, you witnessed the attempted
         kidnapping of some woman you don’t know or recognize. Three, you drop one of the kidnappers—good work, by the way—and run
         for it, keeping this woman with you despite the fact you’re now being shot at.”
      </p>
<p>“How many?”</p>
<p>“Six dead. Twelve wounded, five of them critically. They’re in a different hospital somewhere, in wards a lot less posh than
         this one.” Chain waggled his little finger. “Four, after a tour of central London, you pitch up in a Catholic church. The
         kidnappers enter, then leave without their intended target. They die on the steps—how, I can guess, but the CCTV goes mysteriously
         blank again. Five, I get there. Oshicora’s gone, you’ve gone, the Joan’s gone. Have I got it about right?”
      </p>
<p><a id="page-35"></a>“More or less,” admitted Petrovitch.
      </p>
<p>“So I’ll ask you again: why?” The detective leaned back in his chair, and closed his eyes. A little while later, he murmured,
         “I’m still here.”
      </p>
<p>Petrovitch stroked the end of his nose, and eventually pushed his glasses back up his face. “I don’t know why,” he said.</p>
<p>“You don’t sound so certain of that.”</p>
<p>“I genuinely don’t.” His tone of voice earned him a glance from one heavy-lidded eye.</p>
<p>“Altruism? Chivalry? Civic duty? Random act of kindness? Perhaps you’re a secret crime fighter, and you didn’t have time to
         put your underpants on the outside of your trousers.”
      </p>
<p>“<em>Idi v’zhopu</em>.”
      </p>
<p>“We get them, you know. Costumed vigilantes, and for good or ill, without the superpowers.” Chain shuffled himself more erect,
         and played with the computer in his lap. “They’re just about one step up from the death squads we used to have during Armageddon.
         Were you here for that?”
      </p>
<p>“Before my time, Inspector. Look, I don’t know what I can do for you. I’m the victim of a crime, but the two criminals who
         shot at me and murdered all those people are dead. This Sonja woman…”
      </p>
<p>“Girl. Seventeen.”</p>
<p>“I don’t know her. It was an accident.” Petrovitch scratched at his chest. “Would you rather I’d not done anything?”</p>
<p>Chain said nothing, just looked into the distance with narrowed eyes.</p>
<p>“Oh, you’re joking.” Throwing off the bed covers, <a id="page-36"></a>Petrovitch swung his legs out over the side of the bed. “I’ve walked into someone’s private crusade. So what did they do to
         you? Kill your rookie partner, blow up your car, boil your pet rabbit?”
      </p>
<p>“No,” said Chain. “They just really piss me off.”</p>
<p>“I’m not playing your game, Inspector. You can take your questions and you can shove them up your <em>zhopu</em>.” He found his clothes in the bedside locker. Except his shirt, of course. “Despite the tendency my heart has to stop working
         at critical moments, I quite like the life I have.”
      </p>
<p>He sat on the edge of the mattress and pulled off the hospital’s green gown, dressing as quickly as he could. Chain made no
         effort to stop him, just watched him as he efficiently laced his boots.
      </p>
<p>“I know where to find you,” said Chain as Petrovitch stood warily, testing which way was up. “So, of course, do they.”</p>
<p>“I don’t care.”</p>
<p>“Perhaps you ought. Perhaps you’ll find it harder than you think to pretend all this never happened.” Chain tucked his handheld
         away, and gripped the arms of the chair. He pushed himself up.
      </p>
<p>“I don’t owe them. Quite the reverse.” Petrovitch decided he could make it outside without falling over, and tried his luck.</p>
<p>“My point precisely,” said Chain. He beat Petrovitch to the door handle, and held the door open. “They owe you. This—this
         lovely room, the ambulance, the private doctors, the best of care. That’s just the start.”
      </p>
<p>Petrovitch hesitated, one hand on the wall. “What do you mean?”</p>
<p>“Honor, Petrovitch. You saved Hamano Oshicora’s <a id="page-37"></a>only child from a fate worse than death. You saved both her and the family name. They owe you big time. Why,” he said, “you’re
         almost one of the family yourself now.”
      </p>
<p>“If I don’t have to play along with you, I don’t have to play along with them.”</p>
<p>Chain motioned Petrovitch through the door first. “You’ll find them a lot more persuasive than me.”</p>
<p>“I’m pretty good at saying no.” Petrovitch limped out into the corridor. “Now, if you’ll excuse me. I’m late for work.”</p>
<p>“You’re a student, you don’t get to use that excuse. But I’ll give you a lift if you want.” Chain smiled; it wasn’t pleasant.
         “You get to ride in a police car.”
      </p>
<p>“I’m not a little kid, Inspector.”</p>
<p>“No. You’re a poor immigrant who’s just had a run-in with two of the biggest crime syndicates in the Metrozone and ended up
         in a hospital because your heart is on its last legs. If hearts have legs, of course.”
      </p>
<p>Petrovitch walked away, dismissing the policeman with a wave of his hand. “Yeah. I’ll be fine.”</p>
<p>“It’s not what your doctor said.”</p>
<p>He came back. “What did he say?”</p>
<p>Chain shrugged his shoulders. “If you’re going to discharge yourself without telling anyone, you’ll never find out. Until
         it’s too late.”
      </p>
<p>Petrovitch stared him down.</p>
<p>Chain reached out and tapped Petrovitch’s sternum. “He said you’ve damaged that one beyond repair. You need a replacement.”</p>
<p>“Maybe.”</p>
<p>“You can always ask for a second opinion. But I wouldn’t take too long about it.”</p>
<p>Petrovitch considered matters. “Your bedside manner <a id="page-38"></a>sucks. See you, Inspector.” He turned on his heel and buried his hands in his pockets.
      </p>
<p>“New hearts cost,” called Chain. “You could always ask the Oshicoras to cough up for a replacement, seeing how you wrecked
         the old one in their service.”
      </p>
<p>“Yeah. <em>Perestan mne jabat mozgi svojimi voprosami</em>.” Petrovitch walked to the end of the corridor, past the verdant pot-plants balanced on every window sill, through the doors
         that cut him off from the despondent figure of Detective Inspector Chain.
      </p>
<p>He reached for his wrist and ripped off the hospital tag: somewhere on a computer, the action would have been registered,
         and someone would already be looking for him. Not because he was important, but because the people picking up the bill were.
      </p>
<p>Petrovitch didn’t want to be an asset. He wanted to be invisible again.</p>
<p>He threw the tag into the leaf crown of a fern and caught the first lift down to the ground. He watched the counter topple
         toward zero, and rested his forehead against the cool metal of the wall. By the time he reached the foyer, he’d made his decision.
      </p>
<p>It didn’t look like a hospital. It looked like a hotel, which he supposed it was, really: a hotel with operating theaters.
         It was busy, controlled, efficient. Customers and staff moved through their booking-in procedures with whispered courtesies.
      </p>
<p>Paycops guarded a screen at the ever-revolving door. Even they looked happy and relaxed.</p>
<p>Petrovitch spotted a vacant chair in front of a huge circular desk. He sat down and waited for the clerk behind it to focus
         on him through her holographic screen.
      </p>
<p><a id="page-39"></a>“Good afternoon, sir,” she said accurately: the clock had just tipped past noon. “Welcome to Angel Hope Hospital.”
      </p>
<p>“I need a new heart,” he said baldly. “How much?”</p>
<p>He had her attention. “It very much depends on what is clinically necessary. If you can submit a cardiologist’s report, I
         might be able to book an appointment for you.” While she talked, he could tell she was judging both him and the size of his
         bank balance. “Our transplant teams pride themselves on using only the very latest technology.”
      </p>
<p>“Okay, save me the sales pitch. I knew this day would come sooner or later, so I’ve had a lifetime of weighing up perfectly
         the pros and cons. How much for a vat-grown organic heart?”
      </p>
<p>She smiled sweetly, revealing two rows of perfectly white teeth. “I’m afraid that currently comes in at two hundred and fifty
         thousand euros. Surgery, post-operative care and rehabilitation are extra. I can download a list of charities that might be
         able to help in funding all or part of a less expensive clinical package. We offer several budget solutions that solve most
         chronic cardiac conditions.”
      </p>
<p>Petrovitch was watching carefully for her reaction. He pushed his glasses back up his nose, and asked: “Do you take cash?”</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter004.vue?50f38156